import type { Nullable } from '../../../types';
import { HTTPError, Token } from '../../../entities';
import { TokenStorage } from '../../../storage/token/tokenStorage';
import { TokenRepository } from '../../repositories/tokenRepository';
import { AuthRepository } from '../../repositories/authRepository';
import type { AuthorizationData } from '../../repositories/authRepository';
import { handleErrors } from '../../../helpers';
import AuthDataProvider from './authDataProvider';

export default class AuthRepositoryImpl implements AuthRepository {
  tokenRepository: TokenRepository;

  tokenStorage: TokenStorage;

  refreshTokenPromise: Promise<string> | null = null;

  constructor(tokenRepository: TokenRepository, tokenStorage: TokenStorage) {
    this.tokenRepository = tokenRepository;
    this.tokenStorage = tokenStorage;
  }

  async authorize(data: AuthorizationData): Promise<void> {
    const { tokenRepository, tokenStorage } = this;
    return new Promise((resolve, reject) => {
      tokenRepository
        .getToken(data)
        .then((token: Token) => {
          tokenStorage.saveToken(token);
          resolve();
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async updateToken(): Promise<string> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    const { tokenRepository, tokenStorage } = this;
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return Promise.reject(HTTPError.sessionExpired());
    }

    const data = AuthDataProvider.refreshToken(refreshToken);
    this.refreshTokenPromise = tokenRepository
      .getToken(data)
      .then((token: Token) => {
        tokenStorage.saveToken(token);
        return token.accessToken;
      })
      .catch((err) => {
        tokenStorage.deleteToken();
        const error = handleErrors(err);
        throw error;
      })
      .finally(() => {
        this.refreshTokenPromise = null;
      });

    return this.refreshTokenPromise;
  }

  register(
    email: string,
    password: string,
    cloudflareToken: string
  ): Promise<void> {
    const { tokenRepository, tokenStorage } = this;
    return new Promise((resolve, reject) => {
      tokenRepository
        .getTokenFromRegistration(email, password, cloudflareToken)
        .then((token: Token) => {
          tokenStorage.saveToken(token);
          resolve();
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  deleteToken(): void {
    const { tokenStorage } = this;
    tokenStorage.deleteToken();
  }

  getAccessToken(): Nullable<string> {
    const { tokenStorage } = this;
    return tokenStorage.getAccessToken();
  }

  getRefreshToken(): Nullable<string> {
    const { tokenStorage } = this;
    return tokenStorage.getRefreshToken();
  }
}
